'use client';
import { List, ListItem, ListProps, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import { LendLoader } from '@/components/Loader';

export interface SearchResultsPropsData<T> {
  results: Array<T>;
  renderResult: (_result: T) => ReactNode;
  indexProp: keyof T;
}

export interface SearchResultsPropsPresentation {
  wrapperProps?: ListProps;
}

export interface SearchResultsProps<T>
  extends SearchResultsPropsData<T>,
    SearchResultsPropsPresentation {
  isSearching: boolean;
}

export function SearchResults<T>({
  results,
  renderResult,
  isSearching,
  ...props
}: SearchResultsProps<T>) {
  const theme = useTheme();
  return (
    <>
      <List
        data-component="SearchResults"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'start',
          alignItems: 'start',
          minHeight: '17rem',
          [theme.breakpoints.up('lg')]: {
            padding: '2.5rem',
          },
        }}
      >
        {isSearching ? (
          <ListItem
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: 2,
              alignSelf: 'stretch',
            }}
          >
            <LendLoader />
          </ListItem>
        ) : (
          <>
            {(!results || results?.length === 0) && (
              <ListItem
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 4,
                }}
              >
                <Typography variant="medium">No results found</Typography>
              </ListItem>
            )}
            {results?.length > 0 &&
              results.map((result: T) => {
                const key = result[props.indexProp] as string;
                return (
                  <ListItem
                    key={key}
                    sx={{
                      paddingInline: 2,
                      width: 'auto',
                    }}
                  >
                    {renderResult(result)}
                  </ListItem>
                );
              })}
          </>
        )}
      </List>
    </>
  );
}
